import React, { useEffect } from 'react'
import styled, { css } from 'styled-components'
import { colors } from 'utils/colors'
import { Button, Footer, IconButton, TextButton } from 'components'

import { ButtonVariant } from 'components/Buttons/Button'
import { ButtonComponentType } from 'components/Footer'

import { KeyCode } from 'utils/constants/keyCode'
import { Z_INDEX } from 'utils/constants'

type PositionChoices = 'left' | 'right' | 'middle'

type PopupProps = {
  containerRef?: React.RefObject<HTMLDivElement>
  closePopUp?: () => void
  labelPrimary?: string
  labelPrimaryColor?: keyof typeof colors
  labelSecondary?: string
  onClickPrimary?: () => void
  onClickSecondary?: () => void
  children?: React.ReactNode
  position?: PositionChoices
  primaryButtonIcon?: string | string[]
  primaryButtonIconColor?: keyof typeof colors
  primaryButtonVariant?: ButtonVariant
  fullHeight?: boolean
  width?: string
  title?: React.ReactNode
  minWidth?: string
  isSubmitting?: boolean
  minHeight?: string
  isDisabledPrimary?: boolean
  leftButtonComponent?: ButtonComponentType
  rightButtonComponent?: ButtonComponentType
  isStickyFooter?: boolean
  isShowFooter?: boolean
  addonFooter?: React.ReactNode
  zIndex?: number
  hideBackdrop?: boolean
  disableSecondaryOnSubmitting?: boolean
}

const Popup = ({
  fullHeight = false,
  position = 'middle',
  width = '50vw',
  labelPrimary,
  labelPrimaryColor,
  labelSecondary,
  closePopUp,
  onClickSecondary,
  onClickPrimary,
  children,
  title,
  minWidth,
  isSubmitting,
  minHeight,
  isDisabledPrimary,
  primaryButtonIcon,
  primaryButtonIconColor,
  primaryButtonVariant = 'fill',
  leftButtonComponent,
  rightButtonComponent,
  isStickyFooter = false,
  isShowFooter = true,
  addonFooter,
  containerRef,
  zIndex,
  hideBackdrop = false,
  disableSecondaryOnSubmitting = true,
}: PopupProps) => {
  const shouldShowFooter = isShowFooter && (labelPrimary || labelSecondary)
  const defaultTitle = closePopUp && !title ? <div /> : title

  useEffect(() => {
    const closePopUp = (e: KeyboardEvent) => {
      e.stopPropagation()

      if (e.key === KeyCode.escape && onClickSecondary) {
        onClickSecondary()
      }
    }
    document.addEventListener('keydown', closePopUp)

    return () => {
      document.removeEventListener('keydown', closePopUp)
    }
  }, [onClickSecondary])

  return (
    <PopupOverlay zIndex={zIndex} hideBackdrop={hideBackdrop}>
      <PopUpContent
        ref={containerRef}
        fullHeight={fullHeight}
        position={position}
        width={width}
        minWidth={minWidth}
        minHeight={minHeight}
        hideBackdrop={hideBackdrop}
      >
        {(title || closePopUp) && (
          <TitleWrapper>
            {defaultTitle}
            {closePopUp && <IconButton onClick={closePopUp} icon="times" variant="ghost" />}
          </TitleWrapper>
        )}
        <PopUpContainer>{children}</PopUpContainer>
        {addonFooter && (
          <AddonFooterContainer shouldShowFooter={!!shouldShowFooter}>{addonFooter}</AddonFooterContainer>
        )}
        {shouldShowFooter && (
          <PopUpFooter
            showFooterLine
            isSticky={isStickyFooter}
            leftButtonComponent={
              leftButtonComponent
                ? leftButtonComponent
                : labelSecondary && (
                    <SecondaryButton>
                      <TextButton
                        inline
                        onClick={onClickSecondary}
                        disabled={!onClickSecondary || (disableSecondaryOnSubmitting && isSubmitting)}
                      >
                        {labelSecondary}
                      </TextButton>
                    </SecondaryButton>
                  )
            }
            rightButtonComponent={
              rightButtonComponent
                ? rightButtonComponent
                : labelPrimary && (
                    <PrimaryButton
                      icon={primaryButtonIcon}
                      iconColor={primaryButtonIconColor}
                      color={labelPrimaryColor}
                      inline
                      isSubmitting={isSubmitting}
                      variant={primaryButtonVariant}
                      onClick={onClickPrimary}
                      disabled={!onClickPrimary || isDisabledPrimary}
                    >
                      {labelPrimary}
                    </PrimaryButton>
                  )
            }
          />
        )}
      </PopUpContent>
    </PopupOverlay>
  )
}

const PopupOverlay = styled.div<{ zIndex?: number; hideBackdrop?: boolean }>`
  background-color: ${(p) => (p.hideBackdrop ? 'transparent' : 'rgba(0, 0, 0, 0.4)')};
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  user-select: none;
  width: 100%;
  z-index: ${(p) => p.zIndex ?? Z_INDEX.dropdown};
  pointer-events: ${(p) => (p.hideBackdrop ? 'none' : 'auto')};
`

const PrimaryButton = styled(Button)`
  min-width: 100px;
  padding-left: 20px;
  padding-right: 20px;

  & > svg {
    color: ${(p) => p.theme.colors[p.iconColor ?? 'blue1']};
  }
`

const SecondaryButton = styled.div``

const PopUpContent = styled.div<{
  fullHeight?: boolean
  position?: PositionChoices
  width?: string
  minWidth?: string
  minHeight?: string
  hideBackdrop?: boolean
}>`
  background-color: ${colors.white};
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  position: relative;
  width: ${(p) => p.width};
  min-width: ${(p) => p.minWidth || ''};
  min-height: ${(p) => p.minHeight || ''};
  pointer-events: auto;
  ${(p) =>
    p.fullHeight &&
    css`
      height: 100%;
    `}
  ${({ position }) => {
    if (position === 'left') {
      return css`
        float: left;
        margin: 0 auto 0 0;
      `
    }
    if (position === 'right') {
      return css`
        float: right;
        margin: 0 0 0 auto;
      `
    }

    return css`
      margin: auto;
    `
  }}
  ${(p) => {
    if (!p.hideBackdrop) {
      return `border: none;
              border-radius: 4px;`
    }

    if (p.position === 'right') {
      return `box-shadow: 0px 12px 17px 2px #0000001A;`
    }

    if (p.position === 'left') {
      return `box-shadow: 0px 12px 17px 2px #0000001A;`
    }

    return `
    box-shadow: 0px 12px 17px 2px #0000001A;
    border-radius: 4px;
    `
  }}
`

const TitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: 40px;
  padding: 8px 10px 8px 30px;
  border-bottom: 1px solid ${(p) => p.theme.colors.defaultBorder};

  ${IconButton} {
    margin-left: 20px;
  }
`

const PopUpContainer = styled.div`
  background: white;
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  :is(:only-child) {
    border-radius: 4px;
  }
`

const PopUpFooter = styled(Footer)`
  justify-content: space-between;
  background-color: ${(p) => p.theme.colors.gray2};
`

const AddonFooterContainer = styled.div<{ shouldShowFooter: boolean }>`
  position: absolute;
  bottom: ${(p) => (p.shouldShowFooter ? '60px' : '0')};
  width: 100%;
`

export default Popup
